import { useState, useCallback, useMemo, useRef, useEffect } from "react";
import { Button, Progress, Spinner, Input, Badge } from "reactstrap";
import * as XLSX from "xlsx";
import { useDropzone } from "react-dropzone";
import { useCreateBulkVehicleStock } from "hooks/stockHook";
import ThemeModal from "components/Common/ThemeModal";
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
} from "@tanstack/react-table";

const CHUNK_SIZE = 500

const chunkArray = (array, size) => {
  const chunks = []
  for (let i = 0; i < array.length; i += size) {
    chunks.push(array.slice(i, i + size))
  }
  return chunks
}

// Editable Cell Component
const EditableCell = ({ getValue, row, column, updateData }) => {
  const [value, setValue] = useState(getValue() || "")
  const [isEditing, setIsEditing] = useState(false)
  const inputRef = useRef(null)

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus()
    }
  }, [isEditing])

  const handleBlur = () => {
    setIsEditing(false)
    if (value !== getValue()) {
      updateData(row.index, column.id, value)
    }
  }

  const handleKeyDown = e => {
    if (e.key === "Enter") {
      handleBlur()
    }
    if (e.key === "Escape") {
      setValue(getValue() || "")
      setIsEditing(false)
    }
  }

  if (isEditing) {
    return (
      <Input
        ref={inputRef}
        value={value}
        onChange={e => setValue(e.target.value)}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        style={{
          border: "1px solid #3b82f6",
          background: "#ffffff",
          padding: "0.5rem",
          fontSize: "0.875rem",
          borderRadius: "0.375rem",
          boxShadow: "0 0 0 3px rgba(59, 130, 246, 0.1)",
        }}
      />
    )
  }

  return (
    <div
      onClick={() => setIsEditing(true)}
      style={{
        padding: "0.5rem",
        cursor: "text",
        borderRadius: "0.375rem",
        transition: "all 0.2s",
        minHeight: "38px",
        display: "flex",
        alignItems: "center",
        background: "#ffffff",
        border: "1px solid transparent",
      }}
      className="editable-cell"
      onMouseEnter={e => {
        e.currentTarget.style.background = "#f9fafb"
        e.currentTarget.style.border = "1px solid #e5e7eb"
      }}
      onMouseLeave={e => {
        e.currentTarget.style.background = "#ffffff"
        e.currentTarget.style.border = "1px solid transparent"
      }}
    >
      <span style={{ color: value ? "#111827" : "#9ca3af" }}>
        {value || "Click to edit"}
      </span>
      <i
        className="bx bx-pencil"
        style={{
          fontSize: "0.875rem",
          color: "#9ca3af",
          marginLeft: "auto",
          opacity: 0.5,
        }}
      ></i>
    </div>
  )
}

const BulkVehicleUploader = ({ btnProps = {}, renderTrigger }) => {
  const [isOpen, setIsOpen] = useState(false)
  const toggle = () => setIsOpen(prev => !prev)
  const { mutateAsync } = useCreateBulkVehicleStock()

  const [step, setStep] = useState(1)
  const [rows, setRows] = useState([])
  const [filteredRows, setFilteredRows] = useState([])
  const [fileName, setFileName] = useState("")
  const [progress, setProgress] = useState(0)
  const [summary, setSummary] = useState(null)
  const [failedRows, setFailedRows] = useState([])
  const [loading, setLoading] = useState(false)
  const [pause, setPause] = useState(false)
  const [searchTerm, setSearchTerm] = useState("")

  const uploadRef = useRef({ cancel: false })

  // =============================
  // UPDATE DATA
  // =============================
  const updateData = (rowIndex, columnId, value) => {
    setRows(old => {
      const newRows = [...old]
      newRows[rowIndex] = {
        ...newRows[rowIndex],
        [columnId]: value,
      }
      return newRows
    })

    // Update filtered rows based on search term
    setFilteredRows(old => {
      const newRows = [...rows]
      newRows[rowIndex] = {
        ...newRows[rowIndex],
        [columnId]: value,
      }
      return newRows.filter(r =>
        Object.values(r).some(v =>
          String(v).toLowerCase().includes(searchTerm.toLowerCase())
        )
      )
    })
  }

  const columns = useMemo(() => Object.keys(rows[0] || {}), [rows])

  // =============================
  // RESET
  // =============================
  const handleClose = () => {
    setRows([])
    setFilteredRows([])
    setFileName("")
    setProgress(0)
    setSummary(null)
    setFailedRows([])
    setLoading(false)
    setStep(1)
    setSearchTerm("")
    uploadRef.current.cancel = false
    toggle()
  }

  // =============================
  // TEMPLATE DOWNLOAD
  // =============================
  const downloadTemplate = () => {
    const headers = [
      "full_chassis",
      "brand",
      "model",
      "location",
      "status",
      "exterior_color",
      "package",
      "year",
      "engine_cc",
      "engine_type",
      "key_type",
      "auction_grade",
      "drive_link",
      "short_description",
      "mileage",
      "asking_price",
    ]

    const exampleRow = [
      "ZWE211-8597458",
      "Toyota",
      "Corolla",
      "Dhaka",
      "Available",
      "Pearl",
      "GX",
      "2022",
      "1800",
      "Hybrid",
      "Smart Key",
      "4.5",
      "https://drive.google.com/...",
      "360 Camera, Body Kit",
      "15000",
      "2500000",
    ]

    const worksheet = XLSX.utils.aoa_to_sheet([headers, exampleRow])

    const instructionsSheet = XLSX.utils.aoa_to_sheet([
      ["📋 Instructions"],
      ["• Do not modify column names"],
      ["• full_chassis must be unique"],
      ["• year, mileage, asking_price must be numeric"],
      ["• Remove empty rows before uploading"],
      ["• Date format: YYYY-MM-DD"],
      ["• Boolean values: true/false or 1/0"],
    ])

    const workbook = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(workbook, worksheet, "Vehicle Template")
    XLSX.utils.book_append_sheet(workbook, instructionsSheet, "Instructions")

    XLSX.writeFile(workbook, "vehicle_import_template.xlsx")
  }

  // =============================
  // FILE PARSE
  // =============================
  const parseFile = file => {
    setFileName(file.name)
    const reader = new FileReader()

    reader.onload = event => {
      const data = new Uint8Array(event.target.result)
      const workbook = XLSX.read(data, { type: "array" })
      const sheet = workbook.Sheets[workbook.SheetNames[0]]

      const json = XLSX.utils.sheet_to_json(sheet, { defval: "" })
      setRows(json)
      setFilteredRows(json)
      setStep(2)
    }

    reader.readAsArrayBuffer(file)
  }

  const onDrop = useCallback(files => {
    if (files.length > 0) parseFile(files[0])
  }, [])

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      "text/csv": [".csv"],
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [
        ".xlsx",
      ],
    },
    multiple: false,
  })

  // =============================
  // SEARCH
  // =============================
  const handleSearch = e => {
    const val = e.target.value
    setSearchTerm(val)
    setFilteredRows(
      rows.filter(r =>
        Object.values(r).some(v =>
          String(v).toLowerCase().includes(val.toLowerCase())
        )
      )
    )
  }

  // =============================
  // TABLE CONFIG
  // =============================
  const tableColumns = useMemo(() => {
    return columns.map(col => ({
      accessorKey: col,
      header: col.replace(/_/g, " ").toUpperCase(),
      cell: info => (
        <EditableCell
          getValue={info.getValue}
          row={info.row}
          column={col}
          updateData={updateData}
        />
      ),
    }));
  }, [columns, rows, searchTerm])

  const table = useReactTable({
    data: filteredRows,
    columns: tableColumns,
    getCoreRowModel: getCoreRowModel(),
  })

  // =============================
  // UPLOAD
  // =============================
  const handleUpload = async () => {
    if (!rows.length) return

    setLoading(true)
    setProgress(0)
    setSummary(null)
    setFailedRows([])

    const chunks = chunkArray(rows, CHUNK_SIZE)

    let created = 0
    let updated = 0
    let failed = []

    for (let i = 0; i < chunks.length; i++) {
      if (uploadRef.current.cancel) break
      while (pause) await new Promise(r => setTimeout(r, 500))

      try {
        const res = await mutateAsync({ vehicles: chunks[i] })
        created += res.summary.created_count
        updated += res.summary.updated_count
        failed = [...failed, ...res.failed_rows]
      } catch (error) {
        failed = [
          ...failed,
          ...chunks[i].map(row => ({
            full_chassis: row.full_chassis,
            error: error.message,
          })),
        ]
      }

      setProgress(Math.round(((i + 1) / chunks.length) * 100))
    }

    setSummary({
      total: rows.length,
      created,
      updated,
      failed: failed.length,
      fileName,
      completedAt: new Date().toLocaleString(),
    })

    setFailedRows(failed)
    setStep(3)
    setLoading(false)
  }

  // =============================
  // FAILED EXPORT
  // =============================
  const exportFailedCSV = () => {
    if (!failedRows.length) return
    const header = "full_chassis,error\n"
    const body = failedRows.map(r => `${r.full_chassis},${r.error}`).join("\n")
    const blob = new Blob([header + body], {
      type: "text/csv;charset=utf-8;",
    })
    const link = document.createElement("a")
    link.href = URL.createObjectURL(blob)
    link.download = `failed_rows_${new Date().getTime()}.csv`
    link.click()
  }

  return (
    <>
      {renderTrigger ? (
        renderTrigger({ toggle })
      ) : (
        <Button color="success" onClick={toggle} {...btnProps}>
          <div className="d-flex align-items-center gap-1">
            <i className="bx bx-upload font-size-16"></i>
            <span className="d-none d-sm-inline">Bulk Upload</span>
          </div>
        </Button>
      )}

      <ThemeModal isOpen={isOpen} toggle={handleClose} size="xl">
        <div style={{ padding: "1.5rem" }}>
          {/* Header with Steps */}
          <div style={{ marginBottom: "2rem" }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "0.5rem",
                marginBottom: "1.5rem",
              }}
            >
              <div
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: "50%",
                  background: step >= 1 ? "#059669" : "#e5e7eb",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#ffffff",
                  fontSize: "0.875rem",
                  fontWeight: 600,
                }}
              >
                1
              </div>
              <div
                style={{
                  width: 60,
                  height: 2,
                  background: step >= 2 ? "#059669" : "#e5e7eb",
                }}
              />
              <div
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: "50%",
                  background: step >= 2 ? "#059669" : "#e5e7eb",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#ffffff",
                  fontSize: "0.875rem",
                  fontWeight: 600,
                }}
              >
                2
              </div>
              <div
                style={{
                  width: 60,
                  height: 2,
                  background: step >= 3 ? "#059669" : "#e5e7eb",
                }}
              />
              <div
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: "50%",
                  background: step >= 3 ? "#059669" : "#e5e7eb",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#ffffff",
                  fontSize: "0.875rem",
                  fontWeight: 600,
                }}
              >
                3
              </div>
            </div>
            <div style={{ display: "flex", gap: "4rem", marginLeft: "0.5rem" }}>
              <span
                style={{
                  fontSize: "0.875rem",
                  color: step === 1 ? "#059669" : "#6b7280",
                  fontWeight: step === 1 ? 600 : 400,
                }}
              >
                Upload File
              </span>
              <span
                style={{
                  fontSize: "0.875rem",
                  color: step === 2 ? "#059669" : "#6b7280",
                  fontWeight: step === 2 ? 600 : 400,
                }}
              >
                Review & Edit
              </span>
              <span
                style={{
                  fontSize: "0.875rem",
                  color: step === 3 ? "#059669" : "#6b7280",
                  fontWeight: step === 3 ? 600 : 400,
                }}
              >
                Complete
              </span>
            </div>
          </div>

          {/* Progress Indicator */}
          {loading && (
            <div>
              <div style={{ marginBottom: "0.5rem" }}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    marginBottom: "0.75rem",
                  }}
                >
                  <span
                    style={{
                      fontSize: "0.875rem",
                      color: "#6b7280",
                      display: "flex",
                      alignItems: "center",
                      gap: "0.5rem",
                    }}
                  >
                    <i
                      className="bx bx-loader-alt bx-spin"
                      style={{ color: "#059669" }}
                    ></i>
                    Processing upload...
                  </span>
                  <span
                    style={{
                      fontSize: "0.875rem",
                      fontWeight: 600,
                      color: "#059669",
                      background: "#f0fdf4",
                      padding: "0.25rem 0.75rem",
                      borderRadius: "1rem",
                    }}
                  >
                    {progress}% Complete
                  </span>
                </div>
                <Progress
                  value={progress}
                  style={{
                    height: "0.625rem",
                    borderRadius: "0.5rem",
                    backgroundColor: "#e5e7eb",
                  }}
                  barClassName="bg-success"
                />
              </div>
              <div
                style={{
                  fontSize: "0.75rem",
                  color: "#9ca3af",
                  marginTop: "0.5rem",
                  display: "flex",
                  justifyContent: "space-between",
                }}
              >
                <span>
                  Chunk{" "}
                  {Math.ceil(
                    (progress / 100) * Math.ceil(rows.length / CHUNK_SIZE)
                  )}{" "}
                  of {Math.ceil(rows.length / CHUNK_SIZE)}
                </span>
                <span>{rows.length} total records</span>
              </div>
            </div>
          )}

          {/* Step 1: Upload Area */}
          {step === 1 && (
            <div>
              <div style={{ textAlign: "center" }}>
                <div
                  {...getRootProps()}
                  style={{
                    border: `2px dashed ${
                      isDragActive ? "#059669" : "#d1d5db"
                    }`,
                    borderRadius: "1rem",
                    padding: "3rem",
                    background: isDragActive
                      ? "rgba(5, 150, 105, 0.05)"
                      : "#f9fafb",
                    cursor: "pointer",
                    transition: "all 0.3s ease",
                  }}
                >
                  <input {...getInputProps()} />
                  <i
                    className="bx bx-cloud-upload"
                    style={{
                      fontSize: "4rem",
                      color: isDragActive ? "#059669" : "#9ca3af",
                      marginBottom: "1rem",
                    }}
                  ></i>
                  <p
                    style={{
                      fontSize: "1.125rem",
                      color: "#111827",
                      marginBottom: "0.5rem",
                      fontWeight: 500,
                    }}
                  >
                    {isDragActive
                      ? "Drop your file here"
                      : "Drag & drop your file here"}
                  </p>
                  <p
                    style={{
                      fontSize: "0.875rem",
                      color: "#6b7280",
                      marginBottom: "1rem",
                    }}
                  >
                    or click to browse
                  </p>
                  <Badge
                    color="light"
                    style={{
                      background: "#e5e7eb",
                      color: "#374151",
                      padding: "0.5rem 1rem",
                    }}
                  >
                    Supports: .xlsx, .csv (Max: 100MB)
                  </Badge>
                </div>

                <Button
                  color="link"
                  onClick={downloadTemplate}
                  style={{
                    marginTop: "2rem",
                    color: "#059669",
                    textDecoration: "none",
                    borderBottom: "1px dashed #059669",
                    paddingBottom: "0.25rem",
                    fontSize: "0.875rem",
                  }}
                  className="d-inline-flex align-items-center gap-2"
                >
                  <i className="bx bx-download"></i>
                  Download Template (.xlsx)
                </Button>
              </div>
            </div>
          )}

          {/* Step 2: Preview & Edit */}
          {step === 2 && (
            <div>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: "1.5rem",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "1rem",
                    flex: 1,
                  }}
                >
                  <div
                    style={{
                      position: "relative",
                      maxWidth: 320,
                      width: "100%",
                    }}
                  >
                    <i
                      className="bx bx-search"
                      style={{
                        position: "absolute",
                        left: "1rem",
                        top: "50%",
                        transform: "translateY(-50%)",
                        color: "#9ca3af",
                        fontSize: "1rem",
                        zIndex: 1,
                      }}
                    ></i>
                    <Input
                      placeholder="Search records..."
                      value={searchTerm}
                      onChange={handleSearch}
                      style={{
                        paddingLeft: "2.75rem",
                        borderRadius: "0.5rem",
                        border: "1px solid #e5e7eb",
                        height: "2.75rem",
                        fontSize: "0.875rem",
                      }}
                    />
                  </div>
                  <Badge
                    color="light"
                    style={{
                      background: "#f3f4f6",
                      color: "#374151",
                      padding: "0.5rem 1rem",
                      borderRadius: "2rem",
                      fontSize: "0.875rem",
                      fontWeight: 500,
                    }}
                  >
                    <i className="bx bx-data me-2"></i>
                    {filteredRows.length.toLocaleString()} of{" "}
                    {rows.length.toLocaleString()} records
                  </Badge>
                </div>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "0.5rem",
                  }}
                >
                  <i
                    className="bx bx-info-circle"
                    style={{ color: "#6b7280", fontSize: "1rem" }}
                  ></i>
                  <span style={{ fontSize: "0.875rem", color: "#6b7280" }}>
                    Click any cell to edit
                  </span>
                </div>
              </div>

              <div
                style={{
                  border: "1px solid #e5e7eb",
                  borderRadius: "0.5rem",
                  overflow: "hidden",
                  boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.1)",
                }}
              >
                <div
                  style={{
                    maxHeight: 450,
                    overflow: "auto",
                  }}
                >
                  <table style={{ width: "100%", borderCollapse: "collapse" }}>
                    <thead
                      style={{
                        background: "#f8fafc",
                        borderBottom: "1px solid #e5e7eb",
                        position: "sticky",
                        top: 0,
                        zIndex: 10,
                      }}
                    >
                      {table.getHeaderGroups().map(headerGroup => (
                        <tr key={headerGroup.id}>
                          {headerGroup.headers.map(header => (
                            <th
                              key={header.id}
                              style={{
                                padding: "1rem",
                                textAlign: "left",
                                fontSize: "0.75rem",
                                fontWeight: 600,
                                color: "#374151",
                                textTransform: "uppercase",
                                letterSpacing: "0.05em",
                                borderBottom: "1px solid #e5e7eb",
                                whiteSpace: "nowrap",
                                minWidth: 180,
                              }}
                            >
                              {flexRender(
                                header.column.columnDef.header,
                                header.getContext()
                              )}
                            </th>
                          ))}
                        </tr>
                      ))}
                    </thead>

                    <tbody>
                      {table.getRowModel().rows.length > 0 ? (
                        table.getRowModel().rows.map((row, idx) => (
                          <tr
                            key={row.id}
                            style={{
                              background: idx % 2 === 0 ? "#ffffff" : "#fafafa",
                              transition: "background 0.2s",
                            }}
                            className="hover:bg-gray-50"
                          >
                            {row.getVisibleCells().map(cell => (
                              <td
                                key={cell.id}
                                style={{
                                  padding: "0.5rem",
                                  borderBottom: "1px solid #f3f4f6",
                                }}
                              >
                                {flexRender(
                                  cell.column.columnDef.cell,
                                  cell.getContext()
                                )}
                              </td>
                            ))}
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td
                            colSpan={columns.length}
                            style={{
                              padding: "3rem",
                              textAlign: "center",
                              color: "#6b7280",
                            }}
                          >
                            <i
                              className="bx bx-search"
                              style={{
                                fontSize: "2rem",
                                display: "block",
                                marginBottom: "1rem",
                              }}
                            ></i>
                            No matching records found
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* Step 3: Summary */}
          {step === 3 && summary && (
            <div>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "1rem",
                  marginBottom: "2rem",
                }}
              >
                <div
                  style={{
                    background:
                      summary.failed > 0
                        ? "linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%)"
                        : "linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)",
                    width: 64,
                    height: 64,
                    borderRadius: "1rem",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <i
                    className={`bx ${
                      summary.failed > 0 ? "bx-error-circle" : "bx-check-circle"
                    }`}
                    style={{
                      fontSize: "2.5rem",
                      color: summary.failed > 0 ? "#dc2626" : "#059669",
                    }}
                  ></i>
                </div>
                <div>
                  <h4
                    style={{
                      margin: 0,
                      fontWeight: 600,
                      color: "#111827",
                      marginBottom: "0.25rem",
                    }}
                  >
                    Upload{" "}
                    {summary.failed > 0
                      ? "Completed with Errors"
                      : "Successful"}
                  </h4>
                  <p
                    style={{
                      margin: 0,
                      fontSize: "0.875rem",
                      color: "#6b7280",
                      display: "flex",
                      alignItems: "center",
                      gap: "0.5rem",
                    }}
                  >
                    <i className="bx bx-calendar"></i>
                    {summary.completedAt}
                    <span
                      style={{
                        width: 4,
                        height: 4,
                        background: "#d1d5db",
                        borderRadius: "50%",
                      }}
                    ></span>
                    <i className="bx bx-file"></i>
                    {summary.fileName}
                  </p>
                </div>
              </div>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(4, 1fr)",
                  gap: "1rem",
                  marginBottom: "2rem",
                }}
              >
                <div
                  style={{
                    textAlign: "center",
                    padding: "1.5rem",
                    background: "#f9fafb",
                    borderRadius: "0.75rem",
                  }}
                >
                  <p
                    style={{
                      fontSize: "0.875rem",
                      color: "#6b7280",
                      marginBottom: "0.5rem",
                    }}
                  >
                    Total Records
                  </p>
                  <h3
                    style={{
                      fontSize: "2rem",
                      fontWeight: 600,
                      color: "#111827",
                      margin: 0,
                    }}
                  >
                    {summary.total.toLocaleString()}
                  </h3>
                </div>
                <div
                  style={{
                    textAlign: "center",
                    padding: "1.5rem",
                    background: "#f0fdf4",
                    borderRadius: "0.75rem",
                    border: "1px solid #86efac",
                  }}
                >
                  <p
                    style={{
                      fontSize: "0.875rem",
                      color: "#047857",
                      marginBottom: "0.5rem",
                    }}
                  >
                    Created
                  </p>
                  <h3
                    style={{
                      fontSize: "2rem",
                      fontWeight: 600,
                      color: "#047857",
                      margin: 0,
                    }}
                  >
                    {summary.created.toLocaleString()}
                  </h3>
                </div>
                <div
                  style={{
                    textAlign: "center",
                    padding: "1.5rem",
                    background: "#eff6ff",
                    borderRadius: "0.75rem",
                    border: "1px solid #93c5fd",
                  }}
                >
                  <p
                    style={{
                      fontSize: "0.875rem",
                      color: "#1d4ed8",
                      marginBottom: "0.5rem",
                    }}
                  >
                    Updated
                  </p>
                  <h3
                    style={{
                      fontSize: "2rem",
                      fontWeight: 600,
                      color: "#1d4ed8",
                      margin: 0,
                    }}
                  >
                    {summary.updated.toLocaleString()}
                  </h3>
                </div>
                <div
                  style={{
                    textAlign: "center",
                    padding: "1.5rem",
                    background: "#fef2f2",
                    borderRadius: "0.75rem",
                    border: "1px solid #fecaca",
                  }}
                >
                  <p
                    style={{
                      fontSize: "0.875rem",
                      color: "#b91c1c",
                      marginBottom: "0.5rem",
                    }}
                  >
                    Failed
                  </p>
                  <h3
                    style={{
                      fontSize: "2rem",
                      fontWeight: 600,
                      color: "#b91c1c",
                      margin: 0,
                    }}
                  >
                    {summary.failed.toLocaleString()}
                  </h3>
                </div>
              </div>

              {summary.failed > 0 && (
                <div
                  style={{
                    background: "#fef2f2",
                    padding: "1.5rem",
                    borderRadius: "0.75rem",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <div>
                    <p
                      style={{
                        fontSize: "0.875rem",
                        color: "#b91c1c",
                        marginBottom: "0.25rem",
                        fontWeight: 500,
                      }}
                    >
                      <i className="bx bx-error-circle me-2"></i>
                      {summary.failed} records failed to process
                    </p>
                    <p
                      style={{
                        fontSize: "0.75rem",
                        color: "#9ca3af",
                        margin: 0,
                      }}
                    >
                      Download the error report for details
                    </p>
                  </div>
                  <Button
                    color="danger"
                    onClick={exportFailedCSV}
                    style={{
                      background: "#dc2626",
                      border: "none",
                      padding: "0.75rem 1.5rem",
                      fontSize: "0.875rem",
                      borderRadius: "0.5rem",
                      display: "flex",
                      alignItems: "center",
                      gap: "0.5rem",
                    }}
                  >
                    <i className="bx bx-download"></i>
                    Download Error Report
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div
          style={{
            borderTop: "1px solid #e5e7eb",
            padding: "1.5rem",
            background: "#f9fafb",
            borderRadius: "0 0 0.5rem 0.5rem",
          }}
        >
          <div className="d-flex justify-content-end gap-2">
            <Button
              onClick={handleClose}
              style={{
                background: "transparent",
                border: "1px solid #d1d5db",
                color: "#374151",
                padding: "0.625rem 1.25rem",
                fontSize: "0.875rem",
                borderRadius: "0.5rem",
                fontWeight: 500,
              }}
              className="d-flex align-items-center gap-2"
            >
              <i className="bx bx-x"></i>
              {step === 3 ? "Close" : "Cancel"}
            </Button>

            {step === 2 && (
              <>
                <Button
                  outline
                  onClick={() => setPause(!pause)}
                  style={{
                    border: `1px solid ${pause ? "#059669" : "#f59e0b"}`,
                    color: pause ? "#059669" : "#f59e0b",
                    background: "transparent",
                    padding: "0.625rem 1.25rem",
                    fontSize: "0.875rem",
                    borderRadius: "0.5rem",
                    fontWeight: 500,
                  }}
                  className="d-flex align-items-center gap-2"
                >
                  <i className={`bx ${pause ? "bx-play" : "bx-pause"}`}></i>
                  {pause ? "Resume Upload" : "Pause Upload"}
                </Button>

                <Button
                  color="primary"
                  onClick={handleUpload}
                  disabled={loading}
                  style={{
                    background:
                      "linear-gradient(135deg, #059669 0%, #047857 100%)",
                    border: "none",
                    padding: "0.625rem 1.5rem",
                    fontSize: "0.875rem",
                    borderRadius: "0.5rem",
                    fontWeight: 500,
                    boxShadow: "0 4px 6px -1px rgba(5, 150, 105, 0.2)",
                    opacity: loading ? 0.7 : 1,
                  }}
                  className="d-flex align-items-center gap-2"
                >
                  {loading ? (
                    <>
                      <Spinner size="sm" style={{ borderWidth: "2px" }} />
                      Processing...
                    </>
                  ) : (
                    <>
                      <i className="bx bx-upload"></i>
                      Start Upload
                    </>
                  )}
                </Button>
              </>
            )}
          </div>
        </div>
      </ThemeModal>

      {/* Add global styles */}
      <style>
        {`
          .hover\\:bg-gray-50:hover {
            background-color: #f9fafb !important;
          }
          .focus\\:ring-1:focus {
            outline: none;
            box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
          }
          .focus\\:ring-primary:focus {
            border-color: #3b82f6;
          }
          .editable-cell:hover i {
            opacity: 1 !important;
          }
          ::-webkit-scrollbar {
            width: 8px;
            height: 8px;
          }
          ::-webkit-scrollbar-track {
            background: #f1f1f1;
            border-radius: 4px;
          }
          ::-webkit-scrollbar-thumb {
            background: #c1c1c1;
            border-radius: 4px;
          }
          ::-webkit-scrollbar-thumb:hover {
            background: #a1a1a1;
          }
        `}
      </style>
    </>
  )
}

export default BulkVehicleUploader
